import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, IconButton, Divider, FormInstance, SelectPicker, DatePicker, Grid, Row, Col } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import PageContent from '@/components/PageContent';
import ErrorMessage from '@/components/ErrorMessage';
import Textarea from '@/components/Textarea';
import FetchingSelect from '@/components/FetchingSelect';
import { BaseErrorModel, PageState } from '@/models/core.model';
import { PointAccount, PointAccountStatus, PointAccountStatusList, UpsertPointAccount } from '@/models/point-account.model';
import { upsertPointAccount } from '@/services/point-account.service';
import { getPointTypesLookup } from '@/services/point-type.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const schema = Schema.Model<UpsertPointAccount>({
  name: Schema.Types.StringType().isRequired('This field is required.').maxLength(200),
  description: Schema.Types.StringType().maxLength(2000),
  pointTypeId: Schema.Types.NumberType().isRequired('This field is required.'),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  startsAt: Schema.Types.DateType(),
  endsAt: Schema.Types.DateType(),
});

const emptyModel: UpsertPointAccount = {
  id: undefined,
  name: '',
  description: undefined,
  pointTypeId: undefined,
  status: PointAccountStatus.Draft,
  startsAt: undefined,
  endsAt: undefined,
};

interface PointAccountFormProps {
  formObject?: PointAccount;
  pageState: PageState;
  onSaved?: () => void;
}

const PointAccountForm: React.FC<PointAccountFormProps> = ({ formObject, pageState, onSaved }) => {
  const [formValue, setFormValue] = useState<UpsertPointAccount>(emptyModel);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  // The API refuses to change the point type once the account has any ledger rows (PointAccountTypeLocked).
  const typeLocked = !!formObject && (formObject.totalFunded > 0 || formObject.totalGranted > 0 || formObject.balance != 0);

  useEffect(() => {
    if (pageState == PageState.Edit && formObject) {
      setFormValue({
        id: formObject.id,
        name: formObject.name,
        description: formObject.description,
        pointTypeId: formObject.pointTypeId,
        status: formObject.status,
        startsAt: formObject.startsAt,
        endsAt: formObject.endsAt,
      });
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject, pageState]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await upsertPointAccount({
        ...formValue,
        description: formValue.description || undefined,
        startsAt: formValue.startsAt ?? undefined,
        endsAt: formValue.endsAt ?? undefined,
      });
      if (result.success) {
        toastSuccessMessage('Point account saved');
        if (pageState == PageState.New && result.result?.id) {
          nav(`/point-accounts/${result.result.id}`);
        } else {
          onSaved?.();
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Form name="upsert-point-account" fluid ref={formRef}
        onChange={value => setFormValue(pre => ({ ...pre, ...value }))}
        formValue={formValue}
        model={schema}
        disabled={loading}
      >
        <Grid fluid>
          <Row>
            <Col span={{ xs: 24, xl: 12 }}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control name="description" accepter={Textarea} rows={3} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Point Type</Form.Label>
                <Form.Control name="pointTypeId" accepter={FetchingSelect} block
                  fetchData={(text, page) => getPointTypesLookup(text, page, true)}
                  placeholder="Select a point type"
                  disabled={typeLocked}
                />
                <Form.HelpText>
                  {typeLocked
                    ? 'Locked: the point type cannot change after the account has been funded.'
                    : 'The points this account holds and grants. Locked after the first fund.'}
                </Form.HelpText>
              </Form.Group>
            </Col>
            <Col span={{ xs: 24, xl: 12 }}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Control name="status" accepter={SelectPicker} data={PointAccountStatusList} searchable={false} cleanable={false} block />
                <Form.HelpText>Only Active accounts grant points.</Form.HelpText>
              </Form.Group>
              <Form.Group>
                <Form.Label>Starts At (optional)</Form.Label>
                <Form.Control name="startsAt" accepter={DatePicker} format="yyyy-MM-dd HH:mm" block />
              </Form.Group>
              <Form.Group>
                <Form.Label>Ends At (optional)</Form.Label>
                <Form.Control name="endsAt" accepter={DatePicker} format="yyyy-MM-dd HH:mm" block />
              </Form.Group>
            </Col>
          </Row>
        </Grid>
      </Form>
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      <Divider />
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav('/point-accounts')}>Back</IconButton>
        <IconButton icon={<PageNextIcon />} placement="right" appearance="primary" onClick={handleSubmit} loading={loading}>
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default PointAccountForm;
